import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FiX, FiCheck, FiFileText, FiCheckCircle, FiAlertCircle, FiPlus } from 'react-icons/fi';
import { toast } from 'react-toastify';
import axios from 'axios';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import AddChemicalModal from './AddChemicalModal';
import AddSupplierModal from './AddSupplierModal';
import { 
  BASE_URL, 
  GET_MASTER_DATA, 
  POST_ITEM_MASTER, 
  PUT_ITEM_MASTER,
  GET_MANUFACTURER_MASTER,
  GET_SUPPLIER_MASTER
} from '../utlilities/config';

const FormField = ({ label, required = false, labelWidthClass = 'w-40', children, actionButton }) => (
  <div className="flex items-start gap-3">
    <div className={`${labelWidthClass} flex items-center justify-between pt-1.5`} style={{width: '200px'}}>
      <label className="text-sm font-medium text-black flex items-center">
        <span>{label}:</span>
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {actionButton && <div className="ml-2">{actionButton}</div>}
    </div>
    <div className="w-full">{children}</div>
  </div>
);

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#f9fafb',
    borderColor: state.isFocused ? '#6366f1' : '#b5b6b8ff',
    boxShadow: state.isFocused ? '0 0 0 1px #6366f1' : 'none',
    borderRadius: '0.5rem',
    '&:hover': {
      borderColor: '#6366f1',
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#92c3fcff'
      : state.isFocused
      ? '#92c3fcff'
      : '#ffffff',
    color: state.isSelected ? '#000000ff' : state.isFocused ? '#ffffff' : '#000000',
    fontWeight: '500', 
    '&:hover': {
      backgroundColor: '#92c3fcff',
      color: '#000000ff',
    },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 999,
    borderRadius: '0.5rem',
    overflow: 'hidden',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

const AddItemModal = ({ showModal, setShowModal, onSave, productToEdit = null }) => {
  const [loading, setLoading] = useState(false);
  const [chemicals, setChemicals] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showChemicalModal, setShowChemicalModal] = useState(false);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [chemicalName, setChemicalName] = useState('');
  const [chemicalId, setChemicalId] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const token = localStorage.getItem('accessToken');

  // Store all chemicals in a ref to avoid re-renders
  const allChemicalsRef = useRef([]);
  const allCompaniesRef = useRef([]);
  const allSuppliersRef = useRef([]);

  // Refs for form fields
  const codeRef = useRef(null);
  const nameRef = useRef(null);
  const companyRef = useRef(null);
  const chemicalRef = useRef(null);
  const sectionRef = useRef(null);
  const categoryRef = useRef(null);
  const scheduleRef = useRef(null);
  const packageRef = useRef(null);
  const unitRef = useRef(null);
  const mrpRef = useRef(null);
  const rateRef = useRef(null);
  const supplierRef = useRef(null);
  const shelfRef = useRef(null);
  const hsnCodeRef = useRef(null);
  const barcodeRef = useRef(null);
  const taxCodeRef = useRef(null);
  const reorderLevelRef = useRef(null);
  const remarksRef = useRef(null);
  const isActiveRef = useRef(null);
  const discount1Ref = useRef(null);
  const discount2Ref = useRef(null);
  const discount3Ref = useRef(null);
  const saveButtonRef = useRef(null);

  useEffect(() => {
    if (showModal) {
      getChemicals();
      // Focus on first field after modal opens
      setTimeout(() => {
        codeRef.current && codeRef.current.focus();
      }, 100);
    }
  }, [showModal]);

  useEffect(() => {
    // Prevent wheel and arrow key issues on number inputs
    const handleWheel = (e) => {
      if (e.target.type === 'number') {
        e.target.blur();
      }
    };

    const handleKeyDown = (e) => {
      if (e.target.type === 'number') {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
        }
      }
    };

    document.addEventListener('wheel', handleWheel, { capture: true });
    document.addEventListener('keydown', handleKeyDown, { capture: true });

    return () => {
      document.removeEventListener('wheel', handleWheel, { capture: true });
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, []);

  const getChemicals = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_MASTER_DATA}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        },
      });

      const companyResponse = await axios.get(`${BASE_URL}${GET_MANUFACTURER_MASTER}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        },
      });

      const supplierResponse = await axios.get(`${BASE_URL}${GET_SUPPLIER_MASTER}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'FilterText': '',
        },
      });

      if (companyResponse.status === 200) {
        const companiesData = companyResponse.data.data || [];
        setCompanies(companiesData);
        allCompaniesRef.current = companiesData;
      }

      if (supplierResponse.status === 200) {
        const suppliersData = supplierResponse.data.data || [];
        setSuppliers(suppliersData);
        allSuppliersRef.current = suppliersData;
      }

      if (response.status === 200) {
        const chemicalsData = (response.data.data || []).filter(item => item.Type === 'C');
        allChemicalsRef.current = chemicalsData;
        setChemicals(chemicalsData);
      }
    } catch (error) {
      console.error('Failed to fetch masters data:', error);
      toast.error('Could not load masters data', {
        autoClose: 1000,
        icon: <FiAlertCircle className="text-red-500" />
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    formik.resetForm();
    setShowModal(false);
  };

  // Handle chemical modal
  const handleOpenChemicalModal = () => {
    setChemicalName('');
    setChemicalId(null);
    setShowChemicalModal(true);
  };

  const handleChemicalSaved = async () => {
    // Refresh chemicals data
    await getChemicals();
    setShowChemicalModal(false);
  };

  // Handle supplier modal
  const handleOpenSupplierModal = () => {
    setSelectedSupplier(null);
    setShowSupplierModal(true);
  };

  const handleSupplierModalClose = () => {
    setShowSupplierModal(false);
    setSelectedSupplier(null);
  };

  const handleSupplierSaved = async () => {
    // Refresh suppliers data
    await getChemicals(); // This also fetches suppliers
    handleSupplierModalClose();
  };

  // Handle keyboard navigation between fields
  const handleKeyDown = (e, nextFieldRef, fallbackRef = null) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (nextFieldRef?.current) {
        nextFieldRef.current.focus();
      } else if (fallbackRef?.current) {
        fallbackRef.current.focus();
      }
    }
  };

  // Load options for chemical AsyncSelect with filtering
  const loadChemicalOptions = useCallback((inputValue, callback) => {
    if (!inputValue || inputValue.length < 3) {
      callback([]);
      return;
    }

    setTimeout(() => {
      const filtered = allChemicalsRef.current
        .filter(chemical => 
          chemical.Name && 
          chemical.Name.toLowerCase().includes(inputValue.toLowerCase())
        )
        .slice(0, 100)
        .map(item => ({
          value: item.ID,
          label: item.Name,
        }));

      callback(filtered);
    }, 100);
  }, []);

  // Get selected chemical option for AsyncSelect value prop
  const getSelectedChemicalOption = useCallback((chemicalId) => {
    if (!chemicalId) return null;
    const chemical = allChemicalsRef.current.find(item => item.ID === chemicalId);
    return chemical ? { value: chemical.ID, label: chemical.Name } : null;
  }, []);

  // Load options for supplier AsyncSelect with filtering
  const loadSupplierOptions = useCallback((inputValue, callback) => {
    if (!inputValue || inputValue.length < 2) {
      callback([]);
      return;
    }

    setTimeout(() => {
      const filtered = allSuppliersRef.current
        .filter(supplier => 
          supplier.SupplierName && 
          supplier.SupplierName.toLowerCase().includes(inputValue.toLowerCase())
        )
        .slice(0, 100)
        .map(item => ({
          value: item.SupplierID,
          label: item.SupplierName,
        }));

      callback(filtered);
    }, 100);
  }, []);

  const supplierOptions = suppliers.map(item => ({
    value: item.SupplierID,
    label: item.SupplierName,
  }));

  const companyOptions = companies.map(item => ({
    value: item.Name,
    label: item.Name,
  }));

  const sectionOptions = [
    { value: 'General', label: 'General' },
    { value: 'Medicine', label: 'Medicine' },
    { value: 'Surgicals', label: 'Surgicals' },
  ];

  const categoryOptions = [
    { value: 'Tablets', label: 'Tablets' },
    { value: 'Capsules', label: 'Capsules' },
    { value: 'Injections', label: 'Injections' },
    { value: 'Syrups', label: 'Syrups' },
    { value: 'Ointments', label: 'Ointments' },
    { value: 'Creams', label: 'Creams' },
    { value: 'Gels', label: 'Gels' },
    { value: 'Sprays', label: 'Sprays' },
    { value: 'Drops', label: 'Drops' },
    { value: 'Inhalers', label: 'Inhalers' },
  ];

  const scheduleOptions = [
    { value: 'Schedule H', label: 'Schedule H' },
    { value: 'Schedule H1', label: 'Schedule H1' },
    { value: 'Schedule X', label: 'Schedule X' },
  ];

  const unitOptions = [
    { value: 'Strip', label: 'Strip' },
    { value: 'Bottle', label: 'Bottle' },
    { value: 'Box', label: 'Box' },
  ];

  const taxCodeOptions = [
    { value: '0', label: 'GST 0%' },
    { value: '5', label: 'GST 5%' },
    { value: '12', label: 'GST 12%' },
    { value: '18', label: 'GST 18%' },
    { value: '28', label: 'GST 28%' },
  ];

  // Formik form setup
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ItemCode: productToEdit?.ItemCode || '',
      Item_Name: productToEdit?.Item_Name || '',
      Company: productToEdit?.Company || '',
      Chemical: productToEdit?.Chemical || 0,
      Section: productToEdit?.Section || '',
      Category: productToEdit?.Category || '',
      Schedule: productToEdit?.Schedule || '',
      package: productToEdit?.package ?? '',
      unit: productToEdit?.unit || '',
      MRP: productToEdit?.MRP ?? '',
      Rate: productToEdit?.Rate ?? '',
      Supplier: productToEdit?.Supplier || 0,
      Shelf: productToEdit?.Shelf || '',
      HSNCode: productToEdit?.HSNCode || '',
      Barcode: productToEdit?.Barcode || '',
      TaxCode: productToEdit?.TaxCode || '',
      ReorderLevel: productToEdit?.ReorderLevel ?? '',
      Remarks: productToEdit?.Remarks || '',
      IsActive: productToEdit?.IsActive !== undefined ? productToEdit.IsActive : true,
      Discount1: productToEdit?.Discount1 ?? '',
      Discount2: productToEdit?.Discount2 ?? '',
      Discount3: productToEdit?.Discount3 ?? '',
      TotalStock: productToEdit?.Onhand ?? '',
    },
    validationSchema: Yup.object().shape({
      ItemCode: Yup.string().required('Item code is required'),
      Item_Name: Yup.string().required('Item name is required'),
      Company: Yup.string(),
      Chemical: Yup.string(),
      Section: Yup.string(),
      Category: Yup.string(),
      Schedule: Yup.string(),
      package: Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .typeError('Package must be a number')
        .min(0, 'Package must be positive')
        .nullable(),
      unit: Yup.string(),
      MRP: Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .typeError('MRP must be a number')
        .min(0, 'MRP must be positive')
        .nullable(),
      Rate: Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .typeError('Rate must be a number')
        .min(0, 'Rate must be positive')
        .nullable(),
      Supplier: Yup.string(),
      Shelf: Yup.string(),
      HSNCode: Yup.string(),
      Barcode: Yup.string(),
      TaxCode: Yup.string(),
      ReorderLevel: Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .typeError('Reorder level must be a number')
        .min(0, 'Reorder level must be positive')
        .nullable(),
      Remarks: Yup.string(),
      IsActive: Yup.boolean(),
      Discount1: Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .typeError('Discount must be a number')
        .min(0)
        .max(100, 'Discount must be between 0 and 100')
        .nullable(),
      Discount2: Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .typeError('Discount must be a number')
        .min(0)
        .max(100, 'Discount must be between 0 and 100')
        .nullable(),
      Discount3: Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .typeError('Discount must be a number')
        .min(0)
        .max(100, 'Discount must be between 0 and 100')
        .nullable(),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        
        const productData = {
          ItemCode: values.ItemCode,
          Item_Name: values.Item_Name,
          Company: values.Company,
          Chemical: values.Chemical,
          Section: values.Section,
          Category: values.Category,
          Schedule: values.Schedule,
          Package: parseFloat(values.package) || 0,
          Packing_Size: "",
          Unit: values.unit,
          CostPerTest: 0,
          CostPerTestUnit: "",
          Mrp: parseFloat(values.MRP) || 0,
          Rate: parseFloat(values.Rate) || 0,
          Supplier: values.Supplier,
          Shelf: values.Shelf,
          HsnCode: values.HSNCode,
          Barcode: values.Barcode,
          ItemType: 'Pharmacy',
          TaxCode: values.TaxCode,
          ReorderLevel: parseFloat(values.ReorderLevel) || 0,
          ReorderLevelUnit: "",
          Remarks: values.Remarks,
          Discount1: parseFloat(values.Discount1) || 0,
          Discount2: parseFloat(values.Discount2) || 0,
          Discount3: parseFloat(values.Discount3) || 0,
        };

        if(productToEdit?.id){
          const response = await axios.post(`${BASE_URL}${PUT_ITEM_MASTER}`, productData,  {
            headers: {
              'XApiKey': process.env.REACT_APP_API_KEY,
              'Authorization': `Bearer ${token}`,
              'ItemNo': productToEdit.id,
            },
          });

          if(response.status === 200){
            setLoading(false);
            toast.success('Product updated successfully.', {
              autoClose: 1000,
              icon: <FiCheckCircle className="text-green-500" />
            });
            formik.resetForm();
            setShowModal(false);
            if (onSave) onSave();
          }else{
            setLoading(false);
            toast.error('An error occurred while updating the product.');
          }
        }else{
          const response = await axios.post(`${BASE_URL}${POST_ITEM_MASTER}`, productData,  {
            headers: {
              'XApiKey': process.env.REACT_APP_API_KEY,
              'Authorization': `Bearer ${token}`,
            },
          });
          if(response.status === 200){
            setLoading(false);
            toast.success('Product added successfully.', {
              autoClose: 1000,
              icon: <FiCheckCircle className="text-green-500" />
            });
            formik.resetForm();
            setShowModal(false);
            if (onSave) onSave();
          }else{
            setLoading(false);
            toast.error('An error occurred while adding the product.');
          }
        }
      } catch (error) {
        console.error(error);
        toast.error('An error occurred while saving the product.');
      } finally {
        setLoading(false);
      }
    }
  });

  const handleReset = () => {
    formik.resetForm();
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
      {loading && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
            <p className="text-indigo-600 font-medium">Processing...</p>
          </div>
        </div>
      )}
      
      <div className="bg-white w-4/5 max-w-6xl rounded-2xl shadow-2xl transform transition-transform duration-300 ease-out overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-5 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <FiFileText className="mr-2" /> {productToEdit ? 'Edit Product' : 'Add New Product'}
          </h2>
          <button 
            onClick={handleCancel}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors duration-200"
            aria-label="Close modal"
          >
            <FiX size={20} />
          </button>
        </div>
        
        {/* Form Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={formik.handleSubmit}>
            {/* Row 1: Item Code and Name */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <FormField label="Item Code" required>
                  <>
                    <div className="relative">
                      <input
                        type="text"
                        name="ItemCode"
                        placeholder="Enter Product Code"
                        className={`w-full pl-2 pr-3 py-1 border ${
                          formik.touched.ItemCode && formik.errors.ItemCode ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                        } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                        value={formik.values.ItemCode}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        ref={codeRef}
                        onKeyDown={(e) => handleKeyDown(e, nameRef)}
                      />
                    </div>
                    {formik.touched.ItemCode && formik.errors.ItemCode && (
                      <p className="mt-1 text-xs text-red-500">{formik.errors.ItemCode}</p>
                    )}
                  </>
                </FormField>
              </div>

              <div>
                <FormField label="Item Name" required>
                  <>
                    <div className="relative">
                      <input
                        type="text"
                        name="Item_Name"
                        placeholder="Enter Product Name"
                        className={`w-full pl-2 pr-3 py-1 border ${
                          formik.touched.Item_Name && formik.errors.Item_Name ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                        } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                        value={formik.values.Item_Name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        ref={nameRef}
                        onKeyDown={(e) => handleKeyDown(e, companyRef)}
                      />
                    </div>
                    {formik.touched.Item_Name && formik.errors.Item_Name && (
                      <p className="mt-1 text-xs text-red-500">{formik.errors.Item_Name}</p>
                    )}
                  </>
                </FormField>
              </div>
            </div>

            {/* Row 2: Company and Chemical */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <FormField label="Company">
                  <>
                    <div className="relative">
                      <Select
                        options={companyOptions}
                        placeholder="Select Company"
                        className=""
                        isSearchable
                        isClearable
                        name="Company"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        value={companyOptions.find(option => option.value === formik.values.Company) || null}
                        onChange={(selectedOption) => {
                          formik.setFieldValue('Company', selectedOption ? selectedOption.value : '');
                        }}
                        onBlur={() => formik.setFieldTouched('Company', true)}
                        styles={customSelectStyles}
                        ref={companyRef}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const isMenuOpen = companyRef.current?.state?.menuIsOpen;
                            const focusedOption = companyRef.current?.state?.focusedOption;
                            
                            if (!isMenuOpen && !focusedOption) {
                              e.preventDefault();
                              chemicalRef.current?.focus()
                            } 
                          }
                        }}
                      />
                    </div>
                    {formik.touched.Company && formik.errors.Company && (
                      <p className="mt-1 text-xs text-red-500">{formik.errors.Company}</p>
                    )}
                  </>
                </FormField>
              </div>

              <div>
                <FormField 
                  label="Chemical"
                  actionButton={
                    <button
                      type="button"
                      onClick={handleOpenChemicalModal}
                      className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1 font-medium transition-colors"
                    >
                      <FiPlus size={12} /> New
                    </button>
                  }
                >
                  <>
                    <div className="relative">
                      <AsyncSelect
                        loadOptions={loadChemicalOptions}
                        placeholder="Select Chemical"
                        className=""
                        isSearchable
                        isClearable
                        name="Chemical"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        defaultOptions={false}
                        cacheOptions={true}
                        value={getSelectedChemicalOption(formik.values.Chemical)}
                        onChange={(selectedOption) => {
                          formik.setFieldValue('Chemical', selectedOption ? selectedOption.value : '');
                        }}
                        onBlur={() => formik.setFieldTouched('Chemical', true)}
                        noOptionsMessage={({ inputValue }) => 
                          inputValue.length < 3 
                            ? 'Enter chemical name' 
                            : 'No chemicals found'
                        }
                        loadingMessage={() => 'Searching chemicals...'}
                        styles={{
                          ...customSelectStyles,
                          control: (base, state) => ({
                            ...customSelectStyles.control(base, state),
                            paddingLeft: '2px'
                          })
                        }}
                        ref={chemicalRef}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const isMenuOpen = chemicalRef.current?.state?.menuIsOpen;
                            const focusedOption = chemicalRef.current?.state?.focusedOption;
                            
                            if (!isMenuOpen && !focusedOption) {
                              e.preventDefault();
                              sectionRef.current?.focus()
                            } 
                          }
                        }}
                      />
                    </div>
                    {formik.touched.Chemical && formik.errors.Chemical && (
                      <p className="mt-1 text-xs text-red-500">{formik.errors.Chemical}</p>
                    )}
                  </>
                </FormField>
              </div>
            </div>

            {/* Row 3: Section and Category */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <FormField label="Section">
                  <>
                    <div className="relative">
                      <Select
                        options={sectionOptions}
                        placeholder="Select Section"
                        className=""
                        isSearchable
                        isClearable
                        name="Section"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        value={sectionOptions.find(option => option.value === formik.values.Section) || null}
                        onChange={(selectedOption) => {
                          formik.setFieldValue('Section', selectedOption ? selectedOption.value : '');
                        }}
                        onBlur={() => formik.setFieldTouched('Section', true)}
                        styles={customSelectStyles}
                        ref={sectionRef}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const isMenuOpen = sectionRef.current?.state?.menuIsOpen;
                            const focusedOption = sectionRef.current?.state?.focusedOption;
                            
                            if (!isMenuOpen && !focusedOption) {
                              e.preventDefault();
                              categoryRef.current?.focus()
                            } 
                          }
                        }}
                      />
                    </div>
                    {formik.touched.Section && formik.errors.Section && (
                      <p className="mt-1 text-xs text-red-500">{formik.errors.Section}</p>
                    )}
                  </>
                </FormField>
              </div>

              <div>
                <FormField label="Category">
                  <>
                    <div className="relative">
                      <Select
                        options={categoryOptions}
                        placeholder="Select Category"
                        className=""
                        isSearchable
                        isClearable
                        name="Category"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        value={categoryOptions.find(option => option.value === formik.values.Category) || null}
                        onChange={(selectedOption) => {
                          formik.setFieldValue('Category', selectedOption ? selectedOption.value : '');
                        }}
                        onBlur={() => formik.setFieldTouched('Category', true)}
                        styles={customSelectStyles}
                        ref={categoryRef}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const isMenuOpen = categoryRef.current?.state?.menuIsOpen;
                            const focusedOption = categoryRef.current?.state?.focusedOption;
                            
                            if (!isMenuOpen && !focusedOption) {
                              e.preventDefault();
                              scheduleRef.current?.focus()
                            } 
                          }
                        }}
                      />
                    </div>
                    {formik.touched.Category && formik.errors.Category && (
                      <p className="mt-1 text-xs text-red-500">{formik.errors.Category}</p>
                    )}
                  </>
                </FormField>
              </div>
            </div>

            {/* Row 4: Schedule and Packing Size */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <FormField label="Schedule">
                  <>
                    <div className="relative">
                      <Select
                        options={scheduleOptions}
                        placeholder="Select Schedule"
                        className=""
                        isSearchable
                        isClearable
                        name="Schedule"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        value={scheduleOptions.find(option => option.value === formik.values.Schedule) || null}
                        onChange={(selectedOption) => {
                          formik.setFieldValue('Schedule', selectedOption ? selectedOption.value : '');
                        }}
                        onBlur={() => formik.setFieldTouched('Schedule', true)}
                        styles={customSelectStyles}
                        ref={scheduleRef}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const isMenuOpen = scheduleRef.current?.state?.menuIsOpen;
                            const focusedOption = scheduleRef.current?.state?.focusedOption;
                            
                            if (!isMenuOpen && !focusedOption) {
                              e.preventDefault();
                              packageRef.current?.focus()
                            } 
                          }
                        }}
                      />
                    </div>
                    {formik.touched.Schedule && formik.errors.Schedule && (
                      <p className="mt-1 text-xs text-red-500">{formik.errors.Schedule}</p>
                    )}
                  </>
                </FormField>
              </div>

              <div>
                <FormField label="Packing Size">
                  <>
                    <div className="relative">
                      <input
                        type="number"
                        name="package"
                        placeholder="Enter Package"
                        className={`w-full pl-2 pr-3 py-1 border ${
                          formik.touched.package && formik.errors.package ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                        } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                        value={formik.values.package}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        ref={packageRef}
                        onKeyDown={(e) => handleKeyDown(e, unitRef)}
                      />
                    </div>
                    {formik.touched.package && formik.errors.package && (
                      <p className="mt-1 text-xs text-red-500">{formik.errors.package}</p>
                    )}
                  </>
                </FormField>
              </div>
            </div>

            {/* Row 5: Packing Unit and MRP */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <FormField label="Unit">
                  <>
                    <div className="relative">
                      <Select
                        options={unitOptions}
                        placeholder="Select Unit"
                        className=""
                        isSearchable
                        isClearable
                        name="unit"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        value={unitOptions.find(option => option.value === formik.values.unit) || null}
                        onChange={(selectedOption) => {
                          formik.setFieldValue('unit', selectedOption ? selectedOption.value : '');
                        }}
                        onBlur={() => formik.setFieldTouched('unit', true)}
                        styles={customSelectStyles}
                        ref={unitRef}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const isMenuOpen = unitRef.current?.state?.menuIsOpen;
                            const focusedOption = unitRef.current?.state?.focusedOption;
                            
                            if (!isMenuOpen && !focusedOption) {
                              e.preventDefault();
                              mrpRef.current?.focus()
                            } 
                          }
                        }}
                      />
                    </div>
                    {formik.touched.unit && formik.errors.unit && (
                      <p className="mt-1 text-xs text-red-500">{formik.errors.unit}</p>
                    )}
                  </>
                </FormField>
              </div>

              <div>
                <FormField label="MRP">
                  <>
                    <div className="relative">
                      <input
                        type="number"
                        name="MRP"
                        placeholder="Enter MRP"
                        step="0.01"
                        className={`w-full pl-2 pr-3 py-1 border ${
                          formik.touched.MRP && formik.errors.MRP ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                        } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                        value={formik.values.MRP}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        ref={mrpRef}
                        onKeyDown={(e) => handleKeyDown(e, rateRef)}
                      />
                    </div>
                    {formik.touched.MRP && formik.errors.MRP && (
                      <p className="mt-1 text-xs text-red-500">{formik.errors.MRP}</p>
                    )}
                  </>
                </FormField>
              </div>
            </div>

            {/* Row 6: Rate and Supplier */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <FormField label="Rate">
                  <>
                    <div className="relative">
                      <input
                        type="number"
                        name="Rate"
                        placeholder="Enter Rate"
                        step="0.01"
                        className={`w-full pl-2 pr-3 py-1 border ${
                          formik.touched.Rate && formik.errors.Rate ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                        } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                        value={formik.values.Rate}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        ref={rateRef}
                        onKeyDown={(e) => handleKeyDown(e, supplierRef)}
                      />
                    </div>
                    {formik.touched.Rate && formik.errors.Rate && (
                      <p className="mt-1 text-xs text-red-500">{formik.errors.Rate}</p>
                    )}
                  </>
                </FormField>
              </div>

              <div>
                <FormField 
                  label="Supplier"
                  actionButton={
                    <button
                      type="button"
                      onClick={handleOpenSupplierModal}
                      className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1 font-medium transition-colors"
                    >
                      <FiPlus size={12} /> New
                    </button>
                  }
                >
                  <>
                    <div className="relative">
                      <Select
                        options={supplierOptions}
                        placeholder="Select Supplier"
                        className=""
                        isSearchable
                        isClearable
                        name="Supplier"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        value={supplierOptions.find(option => option.value === formik.values.Supplier) || null}
                        onChange={(selectedOption) => {
                          formik.setFieldValue('Supplier', selectedOption ? selectedOption.value : '');
                        }}
                        onBlur={() => formik.setFieldTouched('Supplier', true)}
                        styles={customSelectStyles}
                        ref={supplierRef}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const isMenuOpen = supplierRef.current?.state?.menuIsOpen;
                            const focusedOption = supplierRef.current?.state?.focusedOption;
                            
                            if (!isMenuOpen && !focusedOption) {
                              e.preventDefault();
                              shelfRef.current?.focus()
                            } 
                          }
                        }}
                      />
                    </div>
                    {formik.touched.Supplier && formik.errors.Supplier && (
                      <p className="mt-1 text-xs text-red-500">{formik.errors.Supplier}</p>
                    )}
                  </>
                </FormField>
              </div>
            </div>

            {/* Row 7: Shelf and HSN Code */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <FormField label="Shelf">
                  <>
                    <div className="relative">
                      <input
                        type="text"
                        name="Shelf"
                        placeholder="Enter Shelf"
                        className={`w-full pl-2 pr-3 py-1 border ${
                          formik.touched.Shelf && formik.errors.Shelf ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                        } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                        value={formik.values.Shelf}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        ref={shelfRef}
                        onKeyDown={(e) => handleKeyDown(e, hsnCodeRef)}
                      />
                    </div>
                    {formik.touched.Shelf && formik.errors.Shelf && (
                      <p className="mt-1 text-xs text-red-500">{formik.errors.Shelf}</p>
                    )}
                  </>
                </FormField>
              </div>

              <div>
                <FormField label="HSN Code">
                  <>
                    <div className="relative">
                      <input
                        type="text"
                        name="HSNCode"
                        placeholder="Enter HSN Code"
                        className={`w-full pl-2 pr-3 py-1 border ${
                          formik.touched.HSNCode && formik.errors.HSNCode ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                        } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                        value={formik.values.HSNCode}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        ref={hsnCodeRef}
                        onKeyDown={(e) => handleKeyDown(e, barcodeRef)}
                      />
                    </div>
                    {formik.touched.HSNCode && formik.errors.HSNCode && (
                      <p className="mt-1 text-xs text-red-500">{formik.errors.HSNCode}</p>
                    )}
                  </>
                </FormField>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <FormField label="Barcode Number">
                  <>
                    <div className="relative">
                      <input
                        type="text"
                        name="Barcode"
                        placeholder="Enter Barcode Number"
                        className={`w-full pl-2 pr-3 py-1 border ${
                          formik.touched.Barcode && formik.errors.Barcode ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                        } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                        value={formik.values.Barcode}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        ref={barcodeRef}
                        onKeyDown={(e) => handleKeyDown(e, taxCodeRef)}
                      />
                    </div>
                    {formik.touched.Barcode && formik.errors.Barcode && (
                      <p className="mt-1 text-xs text-red-500">{formik.errors.Barcode}</p>
                    )}
                  </>
                </FormField>
              </div>

              <div>
                <FormField label="Total Stock">
                  <>
                    <div className="relative">
                      <input
                        type="text"
                        name="TotalStock"
                        placeholder="Enter Total Stock"
                        className={`w-full pl-2 pr-3 py-1 border ${
                          formik.touched.Onhand && formik.errors.Onhand ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                        } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                        value={formik.values.TotalStock}
                        readOnly
                      />
                    </div>
                  </>
                </FormField>
              </div>
            </div>

            {/* Row 9: Tax Code and Reorder Level */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <FormField label="Tax Code">
                  <>
                    <div className="relative">
                      <Select
                        options={taxCodeOptions}
                        placeholder="Select Tax Code"
                        className=""
                        isSearchable
                        isClearable
                        name="TaxCode"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        value={taxCodeOptions.find(option => option.value === formik.values.TaxCode) || null}
                        onChange={(selectedOption) => {
                          formik.setFieldValue('TaxCode', selectedOption ? selectedOption.value : '');
                        }}
                        onBlur={() => formik.setFieldTouched('TaxCode', true)}
                        styles={customSelectStyles}
                        ref={taxCodeRef}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const isMenuOpen = taxCodeRef.current?.state?.menuIsOpen;
                            const focusedOption = taxCodeRef.current?.state?.focusedOption;
                            
                            if (!isMenuOpen && !focusedOption) {
                              e.preventDefault();
                              reorderLevelRef.current?.focus()
                            } 
                          }
                        }}
                      />
                    </div>
                    {formik.touched.TaxCode && formik.errors.TaxCode && (
                      <p className="mt-1 text-xs text-red-500">{formik.errors.TaxCode}</p>
                    )}
                  </>
                </FormField>
              </div>

              <div>
                <FormField label="Reorder Level">
                  <>
                    <div className="relative">
                      <input
                        type="number"
                        name="ReorderLevel"
                        placeholder="Enter Reorder Level"
                        className={`w-full pl-2 pr-3 py-1 border ${
                          formik.touched.ReorderLevel && formik.errors.ReorderLevel ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                        } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                        value={formik.values.ReorderLevel}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        ref={reorderLevelRef}
                        onKeyDown={(e) => handleKeyDown(e, remarksRef)}
                      />
                    </div>
                    {formik.touched.ReorderLevel && formik.errors.ReorderLevel && (
                      <p className="mt-1 text-xs text-red-500">{formik.errors.ReorderLevel}</p>
                    )}
                  </>
                </FormField>
              </div>
            </div>

            {/* Row 10: Remarks and Is Active */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <FormField label="Remarks">
                  <>
                    <div className="relative">
                      <input
                        type="text"
                        name="Remarks"
                        placeholder="Enter Remarks"
                        className={`w-full pl-2 pr-3 py-1 border ${
                          formik.touched.Remarks && formik.errors.Remarks ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                        } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                        value={formik.values.Remarks}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        ref={remarksRef}
                        onKeyDown={(e) => handleKeyDown(e, isActiveRef)}
                      />
                    </div>
                    {formik.touched.Remarks && formik.errors.Remarks && (
                      <p className="mt-1 text-xs text-red-500">{formik.errors.Remarks}</p>
                    )}
                  </>
                </FormField>
              </div>

              <div>
                <FormField label="Is Active">
                  <>
                    <div className="relative">
                      <select
                        name="IsActive"
                        className={`w-full pl-2 pr-2 py-1 border ${
                          formik.touched.IsActive && formik.errors.IsActive ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                        } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors appearance-none`}
                        value={formik.values.IsActive ? 'true' : 'false'}
                        onChange={(e) => formik.setFieldValue('IsActive', e.target.value === 'true')}
                        onBlur={formik.handleBlur}
                        ref={isActiveRef}
                        onKeyDown={(e) => handleKeyDown(e, discount1Ref)}
                      >
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-black">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                      </div>
                    </div>
                    {formik.touched.IsActive && formik.errors.IsActive && (
                      <p className="mt-1 text-xs text-red-500">{formik.errors.IsActive}</p>
                    )}
                  </>
                </FormField>
              </div>
            </div>

            {/* Row 11: Discounts */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div>
                <FormField label="Discount %1" labelWidthClass="w-32">
                  <>
                    <div className="relative">
                      <input
                        type="number"
                        name="Discount1"
                        placeholder="Enter Discount %1"
                        step="0.01"
                        className={`w-full pl-2 pr-3 py-1 border ${
                          formik.touched.Discount1 && formik.errors.Discount1 ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                        } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                        value={formik.values.Discount1}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        ref={discount1Ref}
                        onKeyDown={(e) => handleKeyDown(e, discount2Ref)}
                      />
                    </div>
                    {formik.touched.Discount1 && formik.errors.Discount1 && (
                      <p className="mt-1 text-xs text-red-500">{formik.errors.Discount1}</p>
                    )}
                  </>
                </FormField>
              </div>

              <div>
                <FormField label="Discount %2" labelWidthClass="w-32">
                  <>
                    <div className="relative">
                      <input
                        type="number"
                        name="Discount2"
                        placeholder="Enter Discount %2"
                        step="0.01"
                        className={`w-full pl-2 pr-3 py-1 border ${
                          formik.touched.Discount2 && formik.errors.Discount2 ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                        } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                        value={formik.values.Discount2}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        ref={discount2Ref}
                        onKeyDown={(e) => handleKeyDown(e, discount3Ref)}
                      />
                    </div>
                    {formik.touched.Discount2 && formik.errors.Discount2 && (
                      <p className="mt-1 text-xs text-red-500">{formik.errors.Discount2}</p>
                    )}
                  </>
                </FormField>
              </div>

              <div>
                <FormField label="Discount %3" labelWidthClass="w-32">
                  <>
                    <div className="relative">
                      <input
                        type="number"
                        name="Discount3"
                        placeholder="Enter Discount %3"
                        step="0.01"
                        className={`w-full pl-2 pr-3 py-1 border ${
                          formik.touched.Discount3 && formik.errors.Discount3 ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                        } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                        value={formik.values.Discount3}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        ref={discount3Ref}
                        onKeyDown={(e) => handleKeyDown(e, saveButtonRef)}
                      />
                    </div>
                    {formik.touched.Discount3 && formik.errors.Discount3 && (
                      <p className="mt-1 text-xs text-red-500">{formik.errors.Discount3}</p>
                    )}
                  </>
                </FormField>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                className="px-6 py-1 text-sm font-medium text-black bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-4 focus:ring-gray-200 transition-colors flex items-center"
                onClick={handleReset}
              >
                <FiX className="mr-2" /> Reset
              </button>
              <button
                type="button"
                className="px-6 py-1 text-sm font-medium text-black bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-4 focus:ring-gray-200 transition-colors flex items-center"
                onClick={handleCancel}
              >
                <FiX className="mr-2" /> Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-1 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg hover:from-indigo-700 hover:to-purple-700 focus:ring-4 focus:ring-indigo-300 transition-all shadow-md hover:shadow-lg flex items-center"
                ref={saveButtonRef}
                disabled={loading}
              >
                <FiCheck className="mr-2" /> {productToEdit ? 'Update Product' : 'Save Product'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Chemical Modal */}
      {showChemicalModal && (
        <AddChemicalModal
          setShowModal={setShowChemicalModal}
          chemicalName={chemicalName}
          onSave={handleChemicalSaved}
          chemicalId={chemicalId}
          setChemicalName={setChemicalName}
          setSelectedChemicalId={setChemicalId}
        />
      )}

      {/* Supplier Modal */}
      {showSupplierModal && (
        <AddSupplierModal
          setShowModal={handleSupplierModalClose}
          onSave={handleSupplierSaved}
          selectedItem={selectedSupplier}
          setSelectedItem={setSelectedSupplier}
          mode="Supplier"
        />
      )}
    </div>
  );
};

export default AddItemModal;

